import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';

export interface OAuthResult {
  success: boolean;
  error?: string;
}

const AMAZON_OAUTH_TYPE = 'AMAZON_OAUTH_RESULT';
const DEFAULT_POPUP_WIDTH = 800;
const DEFAULT_POPUP_HEIGHT = 650;

function getCenteredPopupFeatures(width = DEFAULT_POPUP_WIDTH, height = DEFAULT_POPUP_HEIGHT): string {
  const left = Math.max(0, (screen.width - width) / 2);
  const top = Math.max(0, (screen.height - height) / 2);
  return `location=1,width=${width},height=${height},left=${left},top=${top}`;
}

function parseOAuthPayload(payload: any): OAuthResult | null {
  if (!payload || payload.type !== AMAZON_OAUTH_TYPE) {
    return null;
  }
  return {
    success: !!payload.success,
    error: payload.error || undefined,
  };
}

function readStoredOAuthResult(key = AMAZON_OAUTH_TYPE): OAuthResult | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? parseOAuthPayload(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function clearStoredOAuthResult(key = AMAZON_OAUTH_TYPE): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  openQBOPopup(uri: string): void {
    const win = window.open(uri, 'connectPopup', getCenteredPopupFeatures());
    const pollOAuth = window.setInterval(() => {
      try {
        if (win?.document?.URL?.indexOf('code') !== -1) {
          window.clearInterval(pollOAuth);
          win?.close();
          setTimeout(() => location.reload());
        }
      } catch (e) {
        console.log(e);
      }
    }, 1000);
  }

  openASCOPopup(uri: string): Observable<OAuthResult> {
    return new Observable<OAuthResult>((observer) => {
      clearStoredOAuthResult();
      const win = window.open(uri, 'connectPopup', getCenteredPopupFeatures());
      if (!win) {
        observer.next({
          success: false,
          error: 'Popup was blocked. Please allow popups in your browser and try again.',
        });
        observer.complete();
        return;
      }
      return this.subscribeOAuthResult(win, observer);
    });
  }

  private subscribeOAuthResult(win: Window, observer: Observer<OAuthResult>): () => void {
    let handled = false;
    let cleanup: (() => void) | undefined;

    const emitResult = (result: OAuthResult) => {
      if (handled) return;
      handled = true;
      if (cleanup) cleanup();
      observer.next(result);
      observer.complete();
    };

    cleanup = this.bindListeners(win, emitResult);
    return cleanup;
  }

  private bindListeners(win: Window, onResult: (res: OAuthResult) => void): () => void {
    const onMessage = (e: MessageEvent) => {
      const result = parseOAuthPayload(e.data);
      if (result) onResult(result);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === AMAZON_OAUTH_TYPE && e.newValue) {
        try {
          const result = parseOAuthPayload(JSON.parse(e.newValue));
          if (result) onResult(result);
        } catch {}
      }
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);

    const pollInterval = window.setInterval(() => {
      if (!win.closed) return;
      const cached = readStoredOAuthResult();
      onResult(cached || { success: false, error: 'Authorization window was closed.' });
    }, 1000);

    return () => {
      window.clearInterval(pollInterval);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      clearStoredOAuthResult();
    };
  }
}
